import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useState,
  useRef,
} from "react";
import { getSubscriptionStatus } from "~src/api/subscription";
import useAuth from "~src/hooks/useAuth";
import { ActionMap, UserRole } from "~src/types/auth";
import { createAbortController } from "~src/api/common/api";
import { logError } from "~src/utils/logger";
type PermissionContextType = {
  editPermissions: boolean;
  premiumCommunity: boolean;
  accessToNewFeatures: boolean;
  loadSubscriptionStatus: () => Promise<void>;
  plan: string | null;
  planInterval: string | null;
  isCancelledAtPeriodEnd: boolean;
  cancelDate: number | null;
};

type PermissionSchema = {
  editPermissions: boolean; // users with CRUD permission
  premiumCommunity: boolean; // users who can view all aspects
  accessToNewFeatures: boolean; // users who can test the newest features before official release
};

const initialState: PermissionSchema = {
  editPermissions: false,
  premiumCommunity: false,
  accessToNewFeatures: false,
};

enum Plans {
  "Launch" = "Launch",
  "Grow" = "Grow",
  "Scale" = "Scale",
  "Pro" = "Pro",
}

enum Types {
  premiumUser = "PREMIUM_USER",
  editPermission = "EDIT_PERMISSION",
  newFeatureAccess = "ACCESS_TO_NEW_FEATURES",
}

type PermissionPayload = {
  [Types.premiumUser]: {
    plan: string | null;
    userRole: string;
  };
  [Types.editPermission]: {
    userRole: string;
  };
  [Types.newFeatureAccess]: {
    userRole: string;
  };
};

type PermissionActions =
  ActionMap<PermissionPayload>[keyof ActionMap<PermissionPayload>];

const reducer = (state: PermissionSchema, action: PermissionActions) => {
  if (action.type === "PREMIUM_USER") {
    const { plan, userRole } = action.payload;
    return {
      ...state,
      premiumCommunity:
        plan === Plans.Grow ||
        plan === Plans.Scale ||
        plan === Plans.Pro ||
        userRole === UserRole.ADMIN ||
        userRole === UserRole.BETAUSER ||
        userRole === UserRole.BRANDANALYST ||
        userRole === UserRole.TESTER,
    };
  }
  if (action.type === "EDIT_PERMISSION") {
    const { userRole } = action.payload;
    return {
      ...state,
      editPermissions:
        userRole === UserRole.ADMIN || userRole === UserRole.BRANDANALYST,
    };
  }
  if (action.type === "ACCESS_TO_NEW_FEATURES") {
    const { userRole } = action.payload;
    return {
      ...state,
      accessToNewFeatures:
        userRole === UserRole.ADMIN || userRole === UserRole.TESTER,
    };
  }
  return state;
};

const PermissionsContext = createContext<PermissionContextType | null>(null);

const PermissionsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [plan, setPlan] = useState<string | null>(null);
  const [planInterval, setPlanInterval] = useState<string | null>(null);
  const [isCancelledAtPeriodEnd, setIsCancelledAtPeriodEnd] = useState<boolean>(false);
  const [cancelDate, setCancelDate] = useState<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const checkEditPermission = (role: string) => {
    dispatch({
      type: Types.editPermission,
      payload: { userRole: role },
    });
  };

  const checkNewFeatureAccess = (role: string) => {
    dispatch({
      type: Types.newFeatureAccess,
      payload: { userRole: role },
    });
  };

  const checkIsPremiumUser = useCallback(
    (userRole: string) => {
      dispatch({ type: Types.premiumUser, payload: { plan, userRole } });
    },
    [plan],
  );

  const loadSubscriptionStatus = async () => {
    if (!user) {
      setPlan(null);
      setPlanInterval(null);
      setIsCancelledAtPeriodEnd(false);
      setCancelDate(null);
      return;
    }

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new abort controller
    const { controller } = createAbortController();
    abortControllerRef.current = controller;

    try {
      const response = await getSubscriptionStatus({ signal: controller.signal });
      // Only update state if the component is still mounted and request wasn't aborted
      if (!controller.signal.aborted) {
        setPlan(response.current_subscription?.product.name ?? null);
        setPlanInterval(response.current_subscription?.plan.interval ?? null);
        setIsCancelledAtPeriodEnd(response.current_subscription?.cancel_at_period_end ?? false);
        setCancelDate(response.current_subscription?.cancel_at ?? null);
      }
    } catch (error: any) {
      // Only log errors if they're not from cancellation
      if (error.name !== 'CanceledError' && error.message !== 'canceled') {
        logError(`Error loading subscription status: ${error}`);
        setPlan(null);
        setPlanInterval(null);
        setIsCancelledAtPeriodEnd(false);
        setCancelDate(null);
      }
    }
  };

  useEffect(() => {
    loadSubscriptionStatus();
    
    // Cleanup function to abort any pending requests when component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user) {
      checkIsPremiumUser(user.role);
      checkNewFeatureAccess(user.role);
      checkEditPermission(user.role);
    }
  }, [checkIsPremiumUser, user]);

  return (
    <PermissionsContext.Provider
      value={{
        ...state,
        loadSubscriptionStatus,
        plan,
        planInterval,
        isCancelledAtPeriodEnd,
        cancelDate,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export { PermissionsContext, PermissionsProvider };
